import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const remitterAccountCreate = createAsyncThunk(
  "remitterAccountCreate",
  async ({ name, labels }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `${baseUrl}/accounts/remitter`,
        {
          remitter_accounts: { name, labels },
        },
        { headers: getHeaders() },
      );

      if (!res.data?.status) {
        const msg = res.data?.errors?.name?.[0] || res.data?.errors?.base?.[0];

        return rejectWithValue(msg);
      }

      return res.data;
    } catch (error) {
      const msg =
        error?.response?.data?.errors?.name?.[0] ||
        error?.response?.data?.errors?.base?.[0] ||
        error?.message;

      return rejectWithValue(msg);
    }
  },
);

export const remitterAccountEditOtherDetails = createAsyncThunk(
  "remitterAccountEditOtherDetails",
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/edit_other_details`,
        {
          remitter_accounts: {
            name,
          },
        },
        { headers: getHeaders() },
      );

      if (!res.data?.status) {
        const msg = res.data?.errors?.name?.[0] || res.data?.errors?.base?.[0];

        return rejectWithValue(msg);
      }

      return res.data;
    } catch (error) {
      const msg =
        error?.response?.data?.errors?.name?.[0] ||
        error?.response?.data?.errors?.base?.[0] ||
        error?.message;

      return rejectWithValue(msg);
    }
  },
);

export const remitterAccountViewAll = createAsyncThunk(
  "remitterAccountViewAll",
  async (params, { rejectWithValue }) => {
    const {
      page_no,
      page_size,
      search_string,
      start_date,
      end_date,
      labels,
      remitter_account_id,
      approval_status,
      sort_by,
      account_type,
      isDashboardCall,
    } = params;
    try {
      const res = await axiosInstance.get(`${baseUrl}/accounts/remitter`, {
        headers: getHeaders(),
        params: {
          page_no,
          page_size,
          search_string,
          start_date,
          end_date,
          labels,
          remitter_account_id: Array.isArray(remitter_account_id)
            ? remitter_account_id
            : [remitter_account_id],
          approval_status: [approval_status],
          sort_by,
          account_type,
        },
      });
      return { ...res.data, isDashboardCall };
    } catch (error) {
      const msg = error?.response?.data?.errors?.base?.[0];
      CommonToast({
        text: msg,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(msg);
    }
  },
);

export const remitterAccountDetailsById = createAsyncThunk(
  "remitterAccountDetailsById",
  async ({ id }) => {
    try {
      const res = await axiosInstance.get(
        `${baseUrl}/accounts/remitter/${id}`,
        { headers: getHeaders() },
      );
      return res.data;
    } catch (error) {
      const msg =
        error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.errors?.user_unauthorized?.[0];
      CommonToast({
        text: msg,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  },
);

export const remitterAccountApprove = createAsyncThunk(
  "remitterAccountApprove",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/approve`,
        {},
        { headers: getHeaders() },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const remitterAccountReject = createAsyncThunk(
  "remitterAccountReject",
  async ({ id, rejection_reason }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/reject`,
        { rejection_reason: rejection_reason },
        { headers: getHeaders() },
      );

      if (!res.data?.status) {
        return rejectWithValue(res.data);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const remitterAccountArchive = createAsyncThunk(
  "remitterAccountArchive",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/archive`,
        {},
        { headers: getHeaders() },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const remitterAccountUnarchive = createAsyncThunk(
  "remitterAccountUnarchive",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/${id}/unarchive`,
        {},
        { headers: getHeaders() },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addMatchConsolidatedStatement = createAsyncThunk(
  "addMatchConsolidatedStatement",
  async ({ remitter_accounts_ids, mapping_ids }, { rejectWithValue }) => {
    try {
      const mappingIdArray = String(mapping_ids)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const mapping_details = remitter_accounts_ids?.flatMap((remitterId) =>
        mappingIdArray.map((mappingId) => ({
          remitter_accounts_id: String(remitterId),
          mapping_id: String(mappingId),
        })),
      );

      const res = await axiosInstance.post(
        `${baseUrl}/accounts/remitter/consolidated_statement/add_mapping`,
        {
          mapping_details,
        },
        { headers: getHeaders() },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Something went wrong",
      );
    }
  },
);

export const removeUnmatchConsolidatedStatement = createAsyncThunk(
  "removeUnmatchConsolidatedStatement",
  async ({ remitter_accounts_ids, mapping_ids }, { rejectWithValue }) => {
    try {
      const mappingIdArray = String(mapping_ids)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const mapping_details = remitter_accounts_ids?.flatMap((remitterId) =>
        mappingIdArray.map((mappingId) => ({
          remitter_accounts_id: String(remitterId),
          mapping_id: String(mappingId),
        })),
      );

      const res = await axiosInstance.delete(
        `${baseUrl}/accounts/remitter/consolidated_statement/remove_mapping`,
        {
          data: {
            mapping_details,
          },
          headers: getHeaders(),
        },
      );

      return res.data;
    } catch (error) {
      CommonToast({
        text: error?.response?.data || error?.message || error?.base[0],
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  },
);

export const addMatchItemisedStatement = createAsyncThunk(
  "addMatchItemisedStatement",
  async ({ remitter_accounts_ids, mapping_ids }, { rejectWithValue }) => {
    try {
      const mappingIdArray = String(mapping_ids)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const mapping_details = remitter_accounts_ids?.flatMap((remitterId) =>
        mappingIdArray.map((mappingId) => ({
          remitter_accounts_id: String(remitterId),
          mapping_id: String(mappingId),
        })),
      );

      const res = await axiosInstance.post(
        `${baseUrl}/accounts/remitter/consolidated_itemised_statement/add_mapping`,
        {
          mapping_details,
        },
        { headers: getHeaders() },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Something went wrong",
      );
    }
  },
);

export const removeUnmatchItemisedStatement = createAsyncThunk(
  "removeUnmatchItemisedStatement",
  async ({ remitter_accounts_ids, mapping_ids }, { rejectWithValue }) => {
    try {
      const mappingIdArray = String(mapping_ids)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const mapping_details = remitter_accounts_ids?.flatMap((remitterId) =>
        mappingIdArray.map((mappingId) => ({
          remitter_accounts_id: String(remitterId),
          mapping_id: String(mappingId),
        })),
      );

      const res = await axiosInstance.delete(
        `${baseUrl}/accounts/remitter/consolidated_itemised_statement/remove_mapping`,
        {
          data: {
            mapping_details,
          },
          headers: getHeaders(),
        },
      );

      return res.data;
    } catch (error) {
      CommonToast({
        text: error?.response?.data || error?.message || error?.base[0],
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  },
);

export const remitterAddNewLabel = createAsyncThunk(
  "remitterAddNewLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/add_label`,
        data,
        { headers: getHeaders() },
      );
      toast.dismiss();

      const existingLabels = JSON?.parse(
        sessionStorage?.getItem("remitterAccountAvailableLabels") || [],
      );

      const updatedLabels = Array?.from(
        new Set([...existingLabels, ...labels]),
      );

      const payload = JSON?.stringify(updatedLabels);
      sessionStorage?.setItem("remitterAccountAvailableLabels", payload);

      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base?.[0]) {
        CommonToast({
          text: error?.response?.data?.errors?.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else if (error?.response?.data?.ids?.unsuccessful_ids?.[0]?.errors) {
        CommonToast({
          text: error?.response?.data?.ids?.unsuccessful_ids?.[0]?.errors,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else {
        CommonToast({
          text: error.response.data.errors.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      return rejectWithValue(error);
    }
  },
);

export const remitterRemoveNewLabel = createAsyncThunk(
  "remitterRemoveNewLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/remitter/remove_label`,
        data,
        { headers: getHeaders() },
      );
      toast.dismiss();
      return response?.data;
    } catch (error) {
      if (error?.response?.data?.errors?.base) {
        CommonToast({
          text: error.response.data.errors.base[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else if (error?.response?.data?.ids?.unsuccessful_ids?.[0]?.errors) {
        CommonToast({
          text: error?.response?.data?.ids?.unsuccessful_ids?.[0]?.errors,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else {
        CommonToast({
          text: error?.response?.data?.errors?.user_unauthorized[0],
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
      return rejectWithValue(error);
    }
  },
);

export const getAssociatedConsolidatedStatements = createAsyncThunk(
  "getAssociatedConsolidatedStatements",
  async (params) => {
    const {
      page_no,
      page_size,
      remitter_account_id,
      start_date,
      end_date,
      statement_entry_id,
      entity_id,
      min_amount,
      max_amount,
      credit_amount,
    } = params;

    try {
      const res = await axiosInstance.get(
        `${baseUrl}/accounts/remitter/consolidated_statement/mapping`,
        {
          headers: getHeaders(),
          params: {
            page_no: page_no,
            page_size: page_size,
            remitter_account_id: Array.isArray(remitter_account_id)
              ? remitter_account_id
              : [remitter_account_id],
            start_date: start_date,
            end_date: end_date,
            entity_id,
            statement_entry_id: statement_entry_id,
            min_amount,
            max_amount,
            credit_amount: credit_amount,
          },
        },
      );
      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.errors?.base?.[0];
      CommonToast({
        text: msg,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  },
);

export const getAssociatedItemisedStatements = createAsyncThunk(
  "getAssociatedItemisedStatements",
  async (params) => {
    const {
      page_no,
      page_size,
      remitter_account_id,
      start_date,
      end_date,
      ecollection_callback_id,
      credit_amount,
      debit_amount,
      entity_id,
      min_amount,
      max_amount,
    } = params;
    try {
      const res = await axiosInstance.get(
        `${baseUrl}/accounts/remitter/consolidated_itemised_statement/mapping`,
        {
          headers: getHeaders(),
          params: {
            page_no: page_no,
            page_size: page_size,
            remitter_account_id: Array.isArray(remitter_account_id)
              ? remitter_account_id
              : [remitter_account_id],
            start_date,
            end_date,
            entity_id,
            credit_amount: credit_amount || "",
            debit_amount: debit_amount || "",
            ecollection_callback_id: Array.isArray(ecollection_callback_id)
              ? ecollection_callback_id
              : [ecollection_callback_id],
            min_amount,
            max_amount,
          },
        },
      );
      return res.data;
    } catch (error) {
      const msg = error?.response?.data?.errors?.base?.[0];
      CommonToast({
        text: msg,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  },
);

const remitterAccountSlice = createSlice({
  name: "remitterAccount",
  initialState: {
    startDate: null,
    endDate: null,
    toggle: false,
    remitterAccountViewAllData: null,
    remitterAccountViewAllLoading: false,
    remitterAccountViewAllError: null,
    remitterAccountAvailableLabels: [],

    remitterAccountCreateData: null,
    remitterAccountCreateLoading: false,
    remitterAccountCreateError: null,

    remitterAccountEditOtherDetailsData: null,
    remitterAccountEditOtherDetailsLoading: false,
    remitterAccountEditOtherDetailsError: null,

    remitterAccountDetailsData: null,
    remitterAccountDetailsLoading: false,
    remitterAccountDetailsError: null,

    remitterAccountApproveData: null,
    remitterAccountApproveLoading: false,
    remitterAccountApproveError: null,

    remitterAccountRejectData: null,
    remitterAccountRejectLoading: false,
    remitterAccountRejectError: null,

    remitterAccountArchiveData: null,
    remitterAccountArchiveLoading: false,
    remitterAccountArchiveError: null,

    remitterAccountUnarchiveData: null,
    remitterAccountUnarchiveLoading: false,
    remitterAccountUnarchiveError: null,

    addMatchConsolidatedData: null,
    addMatchConsolidatedLoading: false,
    addMatchConsolidatedError: null,

    removeUnmatchConsolidatedData: null,
    removeUnmatchConsolidatedLoading: false,
    removeUnmatchConsolidatedError: null,

    addMatchItemisedData: null,
    addMatchItemisedLoading: false,
    addMatchItemisedError: null,

    removeUnmatchItemisedData: null,
    removeUnmatchItemisedLoading: false,
    removeUnmatchItemisedError: null,

    addLabelsLoading: true,
    addLabelsData: [],
    addLabelsError: false,

    removeLabelsLoading: true,
    removeLabelsData: [],
    removeLabelsError: false,

    getAssociatedItemisedData: [],
    getAssociatedItemisedLoading: false,
    getAssociatedItemisedError: null,

    getAssociatedConsolidatedData: [],
    getAssociatedConsolidatedLoading: false,
    getAssociatedConsolidatedError: null,
  },

  reducers: {
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },

    resetDateRange: (state) => {
      state.startDate = null;
      state.endDate = null;
    },
    resetRemitterDetails: (state) => {
      state.remitterDetails = null;
    },
    resetCreateRemitter(state) {
      state.remitterAccountCreateData = null;
      state.remitterAccountCreateError = null;
    },
    setRemitterAccountDetailsData: (state, action) => {
      state.remitterAccountDetailsData = action.payload;
    },
    setGetConsolidatedAllAccStatementLoading: (state, action) => {
      state.getAssociatedConsolidatedLoading = action.payload;
    },
    resetConsolidatedAllAccStatementData: (state, action) => {
      state.getAssociatedConsolidatedData = null;
    },

    resetItemisedAllAccStatementData: (state, action) => {
      state.getAssociatedItemisedData = null;
    },
    setGetItemisedAllAccStatementLoading: (state, action) => {
      state.getAssociatedItemisedLoading = action.payload;
    },

    resetLabelLoading: (state) => {
      state.addLabelsLoading = false;
      state.removeLabelsLoading = false;
    },
    setAvailabelLabels: (state, action) => {
      state.remitterAccountAvailableLabels = action.payload;
    },
    setRemitterToggle: (state, action) => {
      state.toggle = action.payload;
    },
    resetRemitterToggle: (state) => {
      state.toggle = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(remitterAccountViewAll.pending, (state, action) => {
        state.remitterAccountViewAllLoading = true;
        state.remitterAccountViewAllError = null;
        if (action?.payload?.summary?.available_labels) {
          state.remitterAccountAvailableLabels = [];
        }
      })
      .addCase(remitterAccountViewAll.fulfilled, (state, action) => {
        state.remitterAccountViewAllLoading = false;
        state.remitterAccountViewAllData = action.payload;
        state.remitterAccountAvailableLabels =
          action?.payload?.summary?.available_labels;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "remitterAccountAvailableLabels",
            JSON.stringify(action?.payload?.summary?.available_labels || []),
          );
        }
      })
      .addCase(remitterAccountViewAll.rejected, (state, action) => {
        state.remitterAccountViewAllLoading = false;
        state.remitterAccountViewAllError = action.payload;
        if (action?.payload?.summary?.available_labels) {
          state.remitterAccountAvailableLabels = [];
        }
      })
      .addCase(remitterAccountCreate.pending, (state) => {
        state.remitterAccountCreateLoading = true;
      })
      .addCase(remitterAccountCreate.fulfilled, (state, action) => {
        state.remitterAccountCreateLoading = false;
        state.remitterAccountCreateData = action.payload;
      })
      .addCase(remitterAccountCreate.rejected, (state) => {
        state.remitterAccountCreateError = true;
        state.remitterAccountCreateData = null;
        state.remitterAccountCreateLoading = false;
      })
      .addCase(remitterAccountEditOtherDetails.pending, (state) => {
        state.remitterAccountEditOtherDetailsLoading = true;
        state.remitterAccountEditOtherDetailsError = null;
      })
      .addCase(remitterAccountEditOtherDetails.fulfilled, (state, action) => {
        state.remitterAccountEditOtherDetailsLoading = false;
        state.remitterAccountEditOtherDetailsData = action.payload;
      })
      .addCase(remitterAccountEditOtherDetails.rejected, (state, action) => {
        state.remitterAccountEditOtherDetailsLoading = false;
        state.remitterAccountEditOtherDetailsError = action.payload;
        state.remitterAccountEditOtherDetailsData = null;
      })
      .addCase(remitterAccountDetailsById.pending, (state) => {
        state.remitterAccountDetailsLoading = true;
      })
      .addCase(remitterAccountDetailsById.fulfilled, (state, action) => {
        state.remitterAccountDetailsLoading = false;
        state.remitterAccountDetailsData =
          action.payload?.remitter_accounts?.[0] || null;
      })
      .addCase(remitterAccountDetailsById.rejected, (state, action) => {
        state.remitterAccountDetailsLoading = false;
        state.remitterAccountDetailsError = action.payload;
      })
      .addCase(remitterAccountApprove.pending, (state) => {
        state.remitterAccountApproveLoading = true;
      })
      .addCase(remitterAccountApprove.fulfilled, (state, action) => {
        state.remitterAccountApproveLoading = false;
        state.remitterAccountApproveData = action.payload;
      })
      .addCase(remitterAccountApprove.rejected, (state) => {
        state.remitterAccountApproveError = true;
        state.remitterAccountApproveData = null;
        state.remitterAccountApproveLoading = false;
      })
      .addCase(remitterAccountReject.pending, (state) => {
        state.remitterAccountRejectLoading = true;
      })
      .addCase(remitterAccountReject.fulfilled, (state, action) => {
        state.remitterAccountRejectLoading = false;
        state.remitterAccountRejectData = action.payload;
      })
      .addCase(remitterAccountReject.rejected, (state) => {
        state.remitterAccountRejectError = true;
        state.remitterAccountRejectData = null;
        state.remitterAccountRejectLoading = false;
      })
      .addCase(remitterAccountArchive.pending, (state) => {
        state.remitterAccountArchiveLoading = true;
      })
      .addCase(remitterAccountArchive.fulfilled, (state, action) => {
        state.remitterAccountArchiveLoading = false;
        state.remitterAccountArchiveData = action.payload;
      })
      .addCase(remitterAccountArchive.rejected, (state) => {
        state.remitterAccountArchiveError = true;
        state.remitterAccountArchiveData = null;
        state.remitterAccountArchiveLoading = false;
      })
      .addCase(remitterAccountUnarchive.pending, (state) => {
        state.remitterAccountUnarchiveLoading = true;
      })
      .addCase(remitterAccountUnarchive.fulfilled, (state, action) => {
        state.remitterAccountUnarchiveLoading = false;
        state.remitterAccountUnarchiveData = action.payload;
      })
      .addCase(remitterAccountUnarchive.rejected, (state) => {
        state.remitterAccountUnarchiveError = true;
        state.remitterAccountUnarchiveData = null;
        state.remitterAccountUnarchiveLoading = false;
      })
      .addCase(addMatchConsolidatedStatement.pending, (state) => {
        state.addMatchConsolidatedLoading = true;
        state.addMatchConsolidatedError = null;
      })
      .addCase(addMatchConsolidatedStatement.fulfilled, (state, action) => {
        state.addMatchConsolidatedLoading = false;
        state.addMatchConsolidatedData = action.payload;
      })
      .addCase(addMatchConsolidatedStatement.rejected, (state, action) => {
        state.addMatchConsolidatedLoading = false;
        state.addMatchConsolidatedError = action.payload;
      })
      .addCase(removeUnmatchConsolidatedStatement.pending, (state) => {
        state.removeUnmatchConsolidatedLoading = true;
        state.removeUnmatchConsolidatedError = null;
      })
      .addCase(
        removeUnmatchConsolidatedStatement.fulfilled,
        (state, action) => {
          state.removeUnmatchConsolidatedLoading = false;
          state.removeUnmatchConsolidatedData = action.payload;
        },
      )
      .addCase(removeUnmatchConsolidatedStatement.rejected, (state, action) => {
        state.removeUnmatchConsolidatedLoading = false;
        state.removeUnmatchConsolidatedError = action.payload;
      })
      .addCase(addMatchItemisedStatement.pending, (state) => {
        state.addMatchItemisedLoading = true;
        state.addMatchItemisedError = null;
      })
      .addCase(addMatchItemisedStatement.fulfilled, (state, action) => {
        state.addMatchItemisedLoading = false;
        state.addMatchItemisedData = action.payload;
      })
      .addCase(addMatchItemisedStatement.rejected, (state, action) => {
        state.addMatchItemisedLoading = false;
        state.addMatchItemisedError = action.payload;
      })
      .addCase(removeUnmatchItemisedStatement.pending, (state) => {
        state.removeUnmatchItemisedLoading = true;
        state.removeUnmatchItemisedError = null;
      })
      .addCase(removeUnmatchItemisedStatement.fulfilled, (state, action) => {
        state.removeUnmatchItemisedLoading = false;
        state.removeUnmatchItemisedData = action.payload;
      })
      .addCase(removeUnmatchItemisedStatement.rejected, (state, action) => {
        state.removeUnmatchItemisedLoading = false;
        state.removeUnmatchItemisedError = action.payload;
      })
      .addCase(remitterAddNewLabel.pending, (state, action) => {
        state.addLabelsLoading = true;
      })
      .addCase(remitterAddNewLabel.fulfilled, (state, action) => {
        state.addLabelsLoading = false;
        state.addLabelsData = action.payload;
      })
      .addCase(remitterAddNewLabel.rejected, (state, action) => {
        state.addLabelsLoading = false;
        state.addLabelsError = action.payload;
        state.addLabelsData = null;
      })
      .addCase(remitterRemoveNewLabel.pending, (state, action) => {
        state.removeLabelsLoading = true;
      })
      .addCase(remitterRemoveNewLabel.fulfilled, (state, action) => {
        state.removeLabelsLoading = false;
        state.removeLabelsData = action.payload;
      })
      .addCase(remitterRemoveNewLabel.rejected, (state, action) => {
        state.removeLabelsLoading = false;
        state.removeLabelsError = action.payload;
        state.removeLabelsData = null;
      })
      .addCase(getAssociatedItemisedStatements.pending, (state, action) => {
        state.getAssociatedItemisedLoading = true;
        state.getAssociatedItemisedError = null;
      })
      .addCase(getAssociatedItemisedStatements.fulfilled, (state, action) => {
        state.getAssociatedItemisedLoading = false;
        state.getAssociatedItemisedData = action.payload;
      })
      .addCase(getAssociatedItemisedStatements.rejected, (state, action) => {
        state.getAssociatedItemisedLoading = false;
        state.getAssociatedItemisedError = action.payload;
      })
      .addCase(getAssociatedConsolidatedStatements.pending, (state, action) => {
        state.getAssociatedConsolidatedLoading = true;
        state.getAssociatedConsolidatedError = null;
      })
      .addCase(
        getAssociatedConsolidatedStatements.fulfilled,
        (state, action) => {
          state.getAssociatedConsolidatedLoading = false;
          state.getAssociatedConsolidatedData = action.payload;
        },
      )
      .addCase(
        getAssociatedConsolidatedStatements.rejected,
        (state, action) => {
          state.getAssociatedConsolidatedLoading = false;
          state.getAssociatedConsolidatedError = action.payload;
        },
      );
  },
});

export const {
  resetRemitterDetails,
  resetCreateRemitter,
  setRemitterAccountDetailsData,
  resetLabelLoading,
  resetRemitterToggle,
  setRemitterToggle,
  setGetConsolidatedAllAccStatementLoading,
  setGetItemisedAllAccStatementLoading,
  resetConsolidatedAllAccStatementData,
  resetItemisedAllAccStatementData,
  setStartDate,
  setEndDate,
  resetDateRange,
} = remitterAccountSlice.actions;
export default remitterAccountSlice.reducer;
